import type { BlockInstance, SectionInstance } from "@dk/theme"
import { linkUrl } from "../sections/helpers"

/** Resolving the footer's configuration.
 *
 *  `Theme.layout.footer` was declared, initialised to `[]` and read by NOTHING;
 *  `SiteFooter.vue` rendered from the `ThemeSettings.footer` group instead. Now
 *  both are sources, with the same precedence the header uses: the SECTION's
 *  value when it is set, otherwise the group's. That is what lets the section
 *  ship empty (as all 14 templates do) without moving a pixel.
 *
 *  Extracted from the component so the precedence is unit-testable — the
 *  storefront's vitest runs in node with no DOM package, so a mounted-component
 *  assertion is not available here. */

/** The `footer` section instance, if the theme has one. */
export function footerSection(list: SectionInstance[] | undefined): SectionInstance | undefined {
  return (list ?? []).find((s) => s.type === "footer")
}

/** Section value when set, else the theme-settings group's.
 *
 *  "Set" excludes `undefined` and `""` so that clearing a text field in the
 *  builder falls back rather than forcing an empty string — the same rule the
 *  builder's own seeding uses. `groupKey` differs only for `content_width`,
 *  which the group still calls `width` (a settings group has no shared design
 *  fields to collide with — see packages/theme/src/sections/footer.ts). */
export function footerSetting(
  section: Record<string, unknown> | undefined,
  group: Record<string, unknown> | undefined,
  key: string,
  groupKey: string = key,
): unknown {
  const v = section?.[key]
  return v === undefined || v === "" ? group?.[groupKey] : v
}

/** The bottom-bar line when the seller leaves "Copyright line" empty. One
 *  function because the builder's hint for that field quotes it: the two were
 *  separate strings and the hint said the line stopped at the store name. */
export function defaultCopyright(year: number | string, storeName: string): string {
  return `© ${year} ${storeName} · Powered by Dikafy`
}

const visible = (b: BlockInstance) => b.visible !== false

/** Blocks that occupy a COLUMN in the footer grid.
 *
 *  `page_link` is excluded: Impulse puts it in the bottom bar, which is also the
 *  only placement where repeating the block means anything. Left in the grid it
 *  would render an empty track — a visible hole. */
export function footerColumnBlocks(section: SectionInstance | undefined): BlockInstance[] {
  return (section?.blocks ?? []).filter((b) => visible(b) && b.type !== "page_link")
}

/** `page_link` blocks, as bottom-bar links. Incomplete ones are dropped rather
 *  than rendered as a link to nowhere. */
export function footerBarLinks(section: SectionInstance | undefined): { label: string; url: string }[] {
  return (section?.blocks ?? [])
    .filter((b) => visible(b) && b.type === "page_link")
    .map((b) => ({ label: String(b.settings.label ?? ""), url: linkUrl(b.settings.link) }))
    .filter((l) => l.label !== "" && l.url !== "")
}
